import { PropRuntimeGraph } from '../../../../../shared/types/PropRuntimeGraph';
import { RuntimePropInstance } from '../PropMechanicsTypes';
import { RuntimeNodeBase } from './RuntimeNodeBase';
import { isPropRuntimeGraph, isRuntimePropInstance } from './RuntimeNodeUtils';

type ContainComparableTarget = RuntimePropInstance | PropRuntimeGraph;

/**
 * 概率条件节点。
 *
 * 按配置概率返回 true 或 false。
 */
export class LogicProbabilityNode extends RuntimeNodeBase {
    /**
     * 输出条件端口。
     *
     * @returns true 表示本次随机命中。
     */
    public condition(): boolean {
        const probability = clamp01(Number(this.node.data?.probability ?? 0));
        return this.context.rng.next() < probability;
    }
}

/**
 * 整数比较条件节点。
 *
 * 对左右整数输入执行等于、不等、大于、大于等于、小于、小于等于比较。
 */
export class LogicCompareIntNode extends RuntimeNodeBase {
    /**
     * 输出条件端口。
     *
     * @returns 比较结果。
     */
    public condition(): boolean {
        return compareNumber(
            Math.floor(this.inputNumber('left', 0)),
            Math.floor(this.inputNumber('right', 0)),
            String(this.node.data?.op ?? this.node.data?.operator ?? 'eq'),
        );
    }
}

/**
 * 浮点比较条件节点。
 *
 * 对左右浮点输入执行等于、不等、大于、大于等于、小于、小于等于比较。
 */
export class LogicCompareFloatNode extends RuntimeNodeBase {
    /**
     * 输出条件端口。
     *
     * @returns 比较结果。
     */
    public condition(): boolean {
        return compareNumber(
            this.inputNumber('left', 0),
            this.inputNumber('right', 0),
            String(this.node.data?.op ?? this.node.data?.operator ?? 'eq'),
        );
    }
}

/**
 * 包含条件节点。
 *
 * 判断目标数组中是否包含指定目标实例。
 */
export class LogicContainsNode extends RuntimeNodeBase {
    /**
     * 输出条件端口。
     *
     * @returns true 表示目标数组中包含指定目标。
     */
    public condition(): boolean {
        const targets = this.inputComparableTargets('targets');
        const targetItems = this.inputComparableTargets('target', [this.context.currentTarget ?? this.context.owner]);
        if (!targets.length || !targetItems.length) return false;
        return targets.some((item) => targetItems.some((target) => isSameContainTarget(item, target)));
    }

    private inputComparableTargets(portId: string, fallback: ContainComparableTarget[] = []): ContainComparableTarget[] {
        const result: ContainComparableTarget[] = [];
        for (const value of this.inputValues(portId)) {
            const values = Array.isArray(value) ? value : [value];
            for (const item of values) {
                if (isRuntimePropInstance(item) || isPropRuntimeGraph(item)) result.push(item);
            }
        }
        return result.length ? uniqueComparableTargets(result) : fallback;
    }
}

function compareNumber(left: number, right: number, op: string): boolean {
    if (op === 'ne' || op === 'notEqual') return left !== right;
    if (op === 'gt' || op === 'greater') return left > right;
    if (op === 'gte' || op === 'greaterOrEqual') return left >= right;
    if (op === 'lt' || op === 'less') return left < right;
    if (op === 'lte' || op === 'lessOrEqual') return left <= right;
    return left === right;
}

function clamp01(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.min(1, Math.max(0, value));
}

function isSameContainTarget(left: ContainComparableTarget, right: ContainComparableTarget): boolean {
    if (isRuntimePropInstance(left) && isRuntimePropInstance(right)) return left.id === right.id;
    const leftPrototypeId = readPrototypeId(left);
    const rightPrototypeId = readPrototypeId(right);
    return Boolean(leftPrototypeId && rightPrototypeId && leftPrototypeId === rightPrototypeId);
}

function readPrototypeId(value: ContainComparableTarget): string | undefined {
    if (isRuntimePropInstance(value)) return value.graph._id;
    if (isPropRuntimeGraph(value)) return value._id;
    return undefined;
}

function uniqueComparableTargets(items: ContainComparableTarget[]): ContainComparableTarget[] {
    const seen = new Set<string>();
    const result: ContainComparableTarget[] = [];
    for (const item of items) {
        const key = isRuntimePropInstance(item) ? `instance:${item.id}` : `prototype:${item._id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(item);
    }
    return result;
}
